import { useRollbar } from '@rollbar/react';
import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetFeatures } from 'common/queries/features';
import { useGetAllStudies } from 'common/queries/studies';
import { mergeFeatureGates } from 'utils/features';
import { fetchSelector } from 'common/state/common';
import { FEATURES } from 'common/state/constants';
import { Study } from 'common/types/account';
import { CustomKpi, CustomKpiPayload } from 'common/types/customKpi';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { API_URLS } from 'settings/api';

export const customKpisQueryKey = (studyUuid: string): string[] => ['customKpis', studyUuid];

// The gate hooks live beside the query (not in hooks/features) because hooks/features imports
// hooks/kpis, which reads custom KPIs for labels.
export const useIsCustomKpisEnabledForStudy = (): ((studyUuid?: string) => boolean) => {
  const { data } = useGetFeatures();

  return useCallback(
    (studyUuid?: string) => !!data && !!studyUuid && mergeFeatureGates(data, studyUuid).includes(FEATURES.CUSTOM_KPIS),
    [data],
  );
};

export const useIsCustomKpisEnabled = (studyUuid?: string): boolean => useIsCustomKpisEnabledForStudy()(studyUuid);

// Studies whose custom KPIs can be browsed and edited; empty when the gate is off everywhere.
export const useCustomKpiStudies = (): Study[] => {
  const { data: studies } = useGetAllStudies();
  const isEnabledForStudy = useIsCustomKpisEnabledForStudy();

  return useMemo(() => (studies || []).filter(({ uuid }) => isEnabledForStudy(uuid)), [studies, isEnabledForStudy]);
};

export const useCustomKpis = (studyUuid: string): UseQueryResult<CustomKpi[], unknown> => {
  const { fetchGet } = useRecoilValue(fetchSelector);
  const rollbar = useRollbar();
  const isEnabled = useIsCustomKpisEnabled(studyUuid);

  return useQuery(
    customKpisQueryKey(studyUuid),
    async () => {
      try {
        return await fetchGet<CustomKpi[]>(API_URLS.CUSTOM_KPIS(studyUuid));
      } catch (e: unknown) {
        rollbar.error('useCustomKpis error', { studyUuid, status: (e as { status?: number })?.status });
        throw e;
      }
    },
    {
      enabled: isEnabled,
      // The resource is shared across the account with no locking; refetch to pick up others' edits.
      refetchOnWindowFocus: true,
    },
  );
};

interface UpdateCustomKpiVariables {
  uuid: string;
  payload: CustomKpiPayload;
}

// Errors are rethrown so the builder can show 422 field errors with the draft intact.
export const useCreateCustomKpi = (studyUuid: string): UseMutationResult<CustomKpi, unknown, CustomKpiPayload> => {
  const { fetchPost } = useRecoilValue(fetchSelector);
  const queryClient = useQueryClient();
  const rollbar = useRollbar();

  return useMutation({
    mutationKey: ['createCustomKpi', studyUuid],
    mutationFn: (payload: CustomKpiPayload) => fetchPost<CustomKpi>(API_URLS.CUSTOM_KPIS(studyUuid), { study_uuid: studyUuid, ...payload }),
    onSuccess: () => queryClient.invalidateQueries(customKpisQueryKey(studyUuid)),
    onError: (e: unknown) => {
      rollbar.error('useCreateCustomKpi error', { studyUuid, status: (e as { status?: number })?.status });
    },
  });
};

export const useUpdateCustomKpi = (studyUuid: string): UseMutationResult<CustomKpi, unknown, UpdateCustomKpiVariables> => {
  const { fetchPatch } = useRecoilValue(fetchSelector);
  const queryClient = useQueryClient();
  const rollbar = useRollbar();

  return useMutation({
    mutationKey: ['updateCustomKpi', studyUuid],
    mutationFn: ({ uuid, payload }: UpdateCustomKpiVariables) =>
      fetchPatch<CustomKpi>(API_URLS.CUSTOM_KPI(studyUuid, uuid), { study_uuid: studyUuid, ...payload }),
    // Charts showing the custom KPI hold its values from the old formula (cached for minutes), so refetch them too.
    onSuccess: () => Promise.all([queryClient.invalidateQueries(customKpisQueryKey(studyUuid)), queryClient.invalidateQueries(['chart'])]),
    onError: (e: unknown, { uuid }: UpdateCustomKpiVariables) => {
      rollbar.error('useUpdateCustomKpi error', { studyUuid, customKpiUuid: uuid, status: (e as { status?: number })?.status });
    },
  });
};
